import * as CompactCurrency from "./formatCompactCurrency";
import { getFromLocalStorage, STORAGE_KEYS } from "./LocalStorage";
import Icon from "../../Components/Icon";

/**
 * Returns true if the logged-in user can approve/reject (Data Hub, Pending Approvals).
 * For EXECUTIVE_SPONSOR: when executiveOpsApprove is true, requires executiveOps to also be true.
 *   When executiveOpsApprove is false, buttons are disabled.
 * For other roles (VENDOR_ADMIN, C_LEVEL, EXECUTIVE_SPONSOR_LEAD, IZBA_ADMIN): buttons are enabled.
 */
export const canApproveReject = (userRole?: string | null): boolean => {
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const role = userRole ?? loggedInUser?.role ?? null;
  const normalizedRole = role?.toLowerCase().trim();

  if (normalizedRole === "executive_sponsor") {
    const executiveOpsApprove = loggedInUser?.executiveOpsApprove ?? false;
    if (!executiveOpsApprove) return false;
    return (loggedInUser?.executiveOps ?? false) && executiveOpsApprove;
  }

  return true;
};

export const getInitials = (name: string): string => {
  if (!name) return "";

  const words = name.trim().split(" ").filter(Boolean);
  if (words.length === 1) {
    return words[0][0].toUpperCase();
  }

  const firstInitial = words[0][0];
  const lastInitial = words[words.length - 1][0];

  return (firstInitial + lastInitial).toUpperCase();
};

export const alertFilterTypes = [
  { id: "", label: "All Alerts", icon: "" },
  {
    id: "escalations",
    label: "Escalations",
    icon: <Icon name="escalations" />,
  },
  {
    id: "risks",
    label: "Risks",
    icon: <Icon name="risks" />,
  },
  {
    id: "sentiments",
    label: "Sentiments",
    icon: <Icon name="sentiments" />,
  },
  {
    id: "delegations",
    label: "Delegations",
    icon: <Icon name="delegations" />,
  },
  {
    id: "opportunities",
    label: "Opportunities",
    icon: <Icon name="opportunities" />,
  },
];

export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const severityTypes = {
  // Sentiments
  positive: "POSITIVE",
  negative: "NEGATIVE",

  // Risks
  high: "HIGH",
  medium: "MEDIUM",
  low: "LOW",

  // Opportunities
  large: "LARGE",
  small: "SMALL",

  // Escalations
  critical: "CRITICAL",
  regular: "REGULAR",

  // Delegations
  urgent: "URGENT",
  asap: "ASAP",
};

export const monthList = [
  { label: "Jan", value: 1 },
  { label: "Feb", value: 2 },
  { label: "Mar", value: 3 },
  { label: "Apr", value: 4 },
  { label: "May", value: 5 },
  { label: "Jun", value: 6 },
  { label: "Jul", value: 7 },
  { label: "Aug", value: 8 },
  { label: "Sep", value: 9 },
  { label: "Oct", value: 10 },
  { label: "Nov", value: 11 },
  { label: "Dec", value: 12 },
];

export function formatDateWithTimezone(
  dateStr: string,
  timeZone = "America/New_York"
): string {
  const date = new Date(dateStr);

  const dateOptions: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  };

  const formatter = new Intl.DateTimeFormat("en-US", dateOptions);
  const formatted = formatter.format(date);

  // Extract timezone abbreviation
  const parts = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    timeZoneName: "short",
    timeZone,
  }).formatToParts(date);

  const tzAbbr = parts.find(p => p.type === "timeZoneName")?.value || "EST";

  return `${formatted} (${tzAbbr})`;
}

export const formatDateTime = (
  dateString: string,
  format: "DMY" | "DMY_SHORT" = "DMY"
): { date: string; time: string; dateTime: string } => {
  if (!dateString) return { date: "-", time: "-", dateTime: "-" };

  const date = new Date(dateString);

  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Numeric month
  const shortMonth = date.toLocaleString("default", { month: "short" }); // Abbreviated month name
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");

  const period = hours >= 12 ? "PM" : "AM";

  const formattedDate =
    format === "DMY"
      ? `${day}/${month}/${year}`
      : `${day} ${shortMonth} ${year}`;
  const formattedTime = `${hours}:${minutes} ${period}`;

  return {
    date: formattedDate,
    time: formattedTime,
    dateTime: `${formattedDate} ${formattedTime}`,
  };
};

/** Relative phrase for banners: "just now", "15 minutes ago", "2 hours ago", "yesterday", or "on 09 Sept 2026". */
export const formatRelativeTimeAgo = (
  dateString?: string | null
): string | null => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;

  const now = Date.now();
  const diffMs = now - date.getTime();
  if (diffMs < 0) {
    const formatted = formatDateTime(dateString, "DMY_SHORT").date;
    return formatted !== "-" ? `on ${formatted}` : null;
  }

  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) {
    return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  }

  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;

  const formatted = formatDateTime(dateString, "DMY_SHORT").date;
  return formatted !== "-" ? `on ${formatted}` : null;
};

// Format date in GB format (DD/MM/YYYY, HH:MM:SS) - convenience wrapper
export const formatDateGB = (dateString: string): string => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return dateString;
  }
};

// Format date in US format (MM/DD/YYYY, HH:MM:SS AM/PM) - convenience wrapper
export const formatDateUS = (dateString: string): string => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return dateString;
  }
};

// Format date in US format without seconds (MM/DD/YYYY HH:MM AM/PM)
// If dateOnly is true, returns only MM/DD/YYYY format
export const formatDateUSShort = (
  dateString: string,
  dateOnly = false
): string => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();

    if (dateOnly) {
      return `${month}/${day}/${year}`;
    }

    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    const formattedHours = String(displayHours).padStart(2, "0");

    return `${month}/${day}/${year} ${formattedHours}:${minutes} ${ampm}`;
  } catch {
    return dateString;
  }
};

/**
 * ISO date-only (`YYYY-MM-DD`) → `Aug-30-2026`.
 * Parses the calendar parts directly so UTC midnight does not shift the day.
 */
export const formatIsoDateMonDayYear = (iso?: string | null): string | null => {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const month = monthList[Number(m[2]) - 1]?.label;
  if (!month) return iso;
  return `${month}-${m[3]}-${m[1]}`;
};

export const getValueIcon = (value: string): string | undefined => {
  switch (value?.toUpperCase()) {
    // Sentiments
    case severityTypes.positive:
    case severityTypes.negative:
      return "sentiments";

    // Risks
    case severityTypes.high:
    case severityTypes.medium:
    case severityTypes.low:
      return "risks";

    // Opportunities
    case severityTypes.large:
    case severityTypes.small:
      return "opportunities";

    // Escalations
    case severityTypes.critical:
    case severityTypes.regular:
      return "escalations";

    // Delegations
    case severityTypes.urgent:
    case severityTypes.asap:
      return "delegations";

    default:
      return "sentiments";
  }
};

// Metric formatting utility
export const formatMetricValue = (metric: any): string => {
  if (!metric) return "";

  const { value, format, unit } = metric;

  switch (format) {
    case "percentage":
      return `${value}%`;
    case "currency":
      return CompactCurrency.formatCompactCurrency(value, { fallback: "N/A" });
    case "duration":
      return `${value} ${unit || "Days"}`;
    case "number":
    default:
      return value?.toString();
  }
};

export const alertSteps = [
  { label: "Request Validation", key: "validating" },
  { label: "Validated", key: "validated" },
  { label: "Activate Playbook", key: "activatePlaybook" },
  { label: "Playbook Activated", key: "playbookActivated" },
  { label: "Playbook Completed", key: "playbookCompleted" },
];

export const getUserDisplayName = (
  userId: string | undefined,
  userName: string
): string => {
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);

  if (userId === loggedInUser.userId) {
    return "You";
  }

  return getInitials(userName);
};

// Common search function for filtering by name
export const searchByName = (
  items: any[],
  searchQuery: string,
  nameField = "name"
): any[] => {
  if (!searchQuery || !items) return items;

  const searchLower = searchQuery.toLowerCase();
  return items.filter(item => {
    const name = item[nameField]?.toLowerCase() || "";
    return name.includes(searchLower);
  });
};

export const diffTime = (date: string | Date): string => {
  const now = new Date().getTime();
  const target =
    date instanceof Date ? date.getTime() : new Date(date).getTime();
  const diffMs = Math.abs(target - now); // absolute difference

  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30); // approximate
  const diffYears = Math.floor(diffDays / 365);

  if (diffYears > 0) return diffYears === 1 ? "1 year" : `${diffYears} years`;
  if (diffMonths > 0)
    return diffMonths === 1 ? "1 month" : `${diffMonths} months`;
  if (diffWeeks > 0) return diffWeeks === 1 ? "1 week" : `${diffWeeks} weeks`;
  if (diffDays > 0) return diffDays === 1 ? "1 day" : `${diffDays} days`;

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours > 0) return diffHours === 1 ? "1 hour" : `${diffHours} hours`;

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  if (diffMinutes > 0)
    return diffMinutes === 1 ? "1 minute" : `${diffMinutes} minutes`;

  return "0 days"; // if difference is less than 1 minute
};
